import { useMutation } from "@tanstack/react-query";
import type { InferRequestType, InferResponseType } from "hono";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "../scoped-merchant-client";

// Derived from the route (packages/api/src/resources/assurance/flags/routes.ts) so the request
// and response shapes stay in lock-step with the server instead of being hand-typed here.
const createFlagReq = client.merchant[":merchantId"].assurance.flag.$post;
type CreateFlagRequest = InferRequestType<typeof createFlagReq>["json"];
export type CreateFlagResponse = InferResponseType<typeof createFlagReq>;

type AddMissedRiskPayload = {
  riskId?: string;
  description: string;
};

export const useAddMissedRisk = (videoId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: AddMissedRiskPayload): Promise<CreateFlagResponse | undefined> => {
      if (!scope.merchantId) return;

      // riskId ties the flag to a catalog risk, from which the server derives
      // riskTier/riskTypeShort; no riskId means an uncatalogued risk, defaulted to tier 3 /
      // "Other" — the risk type the server triages in Linear.
      const flagJson: CreateFlagRequest = payload.riskId
        ? { explanation: payload.description, videoId, riskId: payload.riskId }
        : { explanation: payload.description, videoId, riskTier: 3, riskTypeShort: "Other" };

      const flagRes = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.flag.$post({
            json: flagJson,
            param: scope.param,
          })
        : await client.merchant[":merchantId"].assurance.flag.$post({
            json: flagJson,
            param: scope.param,
          });

      if (!flagRes.ok) throw await handleError(flagRes);
      // Ticket raising for the "Other" risk type happens server-side in the same request — see
      // CustomRiskRequestRepo.createFromVideoReview.
      return await flagRes.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["video", scope.merchantId, scope.workspaceId, videoId] });
      queryClient.invalidateQueries({ queryKey: ["video", scope.merchantId] });
      queryClient.invalidateQueries({ queryKey: ["assurance-video", scope.merchantId, scope.workspaceId, videoId] });
    },
  });
};
